import { apiRequest } from './api';
import { state } from './state';
import { getLocale, t } from './i18n';
import type { TranslationKey } from './i18n/types';
import { navigateTo } from './router';
import { forceRecap, showRecapModal, type RecapResponse } from './recap';

/**
 * Раздел уведомлений в шапке: колокольчик с непрочитанными + выпадающая панель.
 * Типы: оценки профиля игрока (rating.*), роли (role.*), привязка к игроку (player.link).
 */

interface NotificationItem {
    id: number;
    type: string;
    message: string;
    link: string | null;
    payload: {
        player_name?: string;
        race?: string;
        tier?: number | null;
        changed?: string[];
        role?: string;
        is_new?: boolean;
        url?: string;
        map_name?: string | null;
        game_id?: number;
        /** Дайджест «пока вас не было» — показывается заново по клику. */
        recap?: RecapResponse;
    } | null;
    actor_name: string | null;
    is_read: boolean;
    created_at: string;
}

interface NotificationsResponse {
    notifications: NotificationItem[];
    unread_count: number;
}

const POLL_MS = 60_000;

// i18n-ключи полей оценки для строки «изменено: …».
const FIELD_KEYS: Record<string, TranslationKey> = {
    race: 'notif.field.race',
    tier: 'notif.field.tier',
    adaptiveness: 'stat.adaptiveness',
    greed: 'stat.greed',
    survival: 'stat.survival',
    turtle: 'stat.turtle',
    aggression: 'stat.aggression',
    variety: 'stat.variety',
};

let unreadCount = 0;
let panelOpen = false;
let pollTimer: number | null = null;

// Актуальные DOM-узлы текущей шапки (пересоздаются при каждом renderUserBox).
let panelEl: HTMLElement | null = null;
let listEl: HTMLElement | null = null;
let badgeEl: HTMLElement | null = null;

/** Глобальная инициализация: закрытие панели по клику вне + опрос непрочитанных. */
export function setupNotifications(): void {
    document.addEventListener('click', (e) => {
        if (!panelOpen) return;
        const target = e.target as Node | null;
        if (panelEl && target && !panelEl.contains(target)) closePanel();
    });

    window.addEventListener('session:changed', () => {
        unreadCount = 0;
        if (!state.token) closePanel();
        updateBadge();
        if (state.token) void refreshUnread();
    });

    if (pollTimer !== null) window.clearInterval(pollTimer);
    pollTimer = window.setInterval(() => {
        if (state.token && !panelOpen) void refreshUnread();
    }, POLL_MS);

    // Позиция панели привязана к шапке — при скролле/ресайзе просто закрываем
    // (скролл внутри самой панели игнорируем).
    window.addEventListener(
        'scroll',
        (e) => {
            const target = e.target;
            if (panelEl && target instanceof Node && panelEl.contains(target)) return;
            closePanel();
        },
        true,
    );
    window.addEventListener('resize', () => closePanel());

    if (state.token) void refreshUnread();
}

/** Добавляет колокольчик в шапку (вызывается из renderUserBox для залогиненных). */
export function mountNotifications(box: HTMLElement): void {
    if (!state.token || !state.user) return;

    const wrap = document.createElement('div');
    wrap.className = 'notif';

    const bell = document.createElement('button');
    bell.type = 'button';
    bell.className = 'notif-bell';
    bell.title = t('notif.title');
    bell.setAttribute('aria-label', t('notif.title'));
    bell.innerHTML = '<span class="notif-bell-icon">🔔</span><span class="notif-badge hidden">0</span>';
    badgeEl = bell.querySelector<HTMLElement>('.notif-badge');

    const panel = document.createElement('div');
    panel.className = 'notif-panel hidden';

    const head = document.createElement('div');
    head.className = 'notif-head';
    const title = document.createElement('span');
    title.className = 'notif-head-title';
    title.textContent = t('notif.title');
    const markAll = document.createElement('button');
    markAll.type = 'button';
    markAll.className = 'notif-mark-all';
    markAll.textContent = t('notif.mark_all');
    markAll.addEventListener('click', (e) => {
        e.stopPropagation();
        void markAllRead();
    });
    head.append(title, markAll);

    const list = document.createElement('div');
    list.className = 'notif-list';
    listEl = list;

    panel.append(head, list);
    wrap.append(bell, panel);
    box.appendChild(wrap);

    // Тестовая кнопка админа: принудительно показывает «пока вас не было».
    if (state.user.is_admin) box.appendChild(buildRecapTestButton());

    panelEl = panel;

    bell.addEventListener('click', (e) => {
        e.stopPropagation();
        if (panelOpen) {
            closePanel();
        } else {
            openPanel();
        }
    });

    updateBadge();

    // После перерисовки шапки сохраняем открытую панель и обновляем список.
    if (panelOpen) {
        positionPanel();
        panel.classList.remove('hidden');
        void loadList();
    }
}

/** Кнопка-тест рядом с колокольчиком (только админ): вызвать «пока вас не было». */
function buildRecapTestButton(): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'notif-test-recap';
    btn.title = t('recap.test');
    btn.setAttribute('aria-label', t('recap.test'));
    btn.textContent = '🕘';
    btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        btn.disabled = true;
        try {
            await forceRecap();
        } catch {
            btn.classList.add('is-error');
            window.setTimeout(() => btn.classList.remove('is-error'), 1200);
        } finally {
            btn.disabled = false;
        }
    });
    return btn;
}

function openPanel(): void {
    panelOpen = true;
    positionPanel();
    panelEl?.classList.remove('hidden');
    void loadList();
}

/** Панель фиксированная: шапка клипует overflow, поэтому ставим по координатам колокольчика. */
function positionPanel(): void {
    if (!panelEl) return;
    const bell = panelEl.parentElement?.querySelector<HTMLElement>('.notif-bell');
    if (!bell) return;
    const rect = bell.getBoundingClientRect();
    panelEl.style.top = `${Math.round(rect.bottom + 8)}px`;
    panelEl.style.right = `${Math.max(8, Math.round(window.innerWidth - rect.right))}px`;
}

function closePanel(): void {
    panelOpen = false;
    panelEl?.classList.add('hidden');
}

async function refreshUnread(): Promise<void> {
    if (!state.token) return;
    try {
        const res = await apiRequest<NotificationsResponse>('/api/notifications?limit=1', {
            token: state.token,
        });
        unreadCount = res.unread_count ?? 0;
        updateBadge();
    } catch {
        /* сеть/сервер — тихо, попробуем на следующем опросе */
    }
}

async function loadList(): Promise<void> {
    if (!state.token || !listEl) return;
    try {
        const res = await apiRequest<NotificationsResponse>('/api/notifications?limit=50', {
            token: state.token,
        });
        unreadCount = res.unread_count ?? 0;
        updateBadge();
        renderList(res.notifications ?? []);
    } catch {
        showListMessage(t('notif.load_error'));
    }
}

function updateBadge(): void {
    if (!badgeEl) return;
    const visible = unreadCount > 0;
    badgeEl.textContent = unreadCount > 99 ? '99+' : String(unreadCount);
    badgeEl.classList.toggle('hidden', !visible);
}

function showListMessage(text: string): void {
    if (!listEl) return;
    listEl.innerHTML = '';
    const empty = document.createElement('div');
    empty.className = 'notif-empty';
    empty.textContent = text;
    listEl.appendChild(empty);
}

function renderList(items: NotificationItem[]): void {
    if (!listEl) return;
    listEl.innerHTML = '';

    if (items.length === 0) {
        showListMessage(t('notif.empty'));
        return;
    }

    for (const item of items) {
        const el = document.createElement('div');
        el.className = 'notif-item';
        if (!item.is_read) el.classList.add('is-unread');

        // Основная кликабельная часть: заголовок/подпись/время
        const main = document.createElement('button');
        main.type = 'button';
        main.className = 'notif-item-main';
        main.addEventListener('click', () => {
            void openNotification(item);
        });

        const head = document.createElement('div');
        head.className = 'notif-item-title';
        head.textContent = notificationTitle(item);

        const meta = document.createElement('div');
        meta.className = 'notif-item-meta';
        meta.textContent = notificationMeta(item);

        const time = document.createElement('div');
        time.className = 'notif-item-time';
        time.textContent = formatTime(item.created_at);

        main.append(head);
        if (meta.textContent) main.append(meta);
        main.append(time);

        // Удалить можно любое своё уведомление (в т.ч. непрочитанное)
        const del = document.createElement('button');
        del.type = 'button';
        del.className = 'notif-item-del';
        del.title = t('notif.delete');
        del.setAttribute('aria-label', t('notif.delete'));
        del.textContent = '✕';
        del.addEventListener('click', (ev) => {
            ev.stopPropagation();
            void deleteNotification(item, el);
        });

        el.append(main, del);
        listEl.appendChild(el);
    }
}

/** Удаление уведомления с оптимистичным удалением строки из списка. */
async function deleteNotification(item: NotificationItem, el: HTMLElement): Promise<void> {
    if (!state.token) return;

    el.remove();
    if (!item.is_read) {
        unreadCount = Math.max(0, unreadCount - 1);
        updateBadge();
    }
    if (listEl && !listEl.querySelector('.notif-item')) {
        showListMessage(t('notif.empty'));
    }

    try {
        await apiRequest(`/api/notifications/${item.id}`, {
            method: 'DELETE',
            token: state.token,
        });
    } catch {
        // Не удалось — вернём список к состоянию сервера.
        void loadList();
    }
}

/** Заголовок уведомления: собираем из типа/данных, чтобы работала локализация. */
function notificationTitle(item: NotificationItem): string {
    const actor = item.actor_name?.trim() || t('notif.anonymous');
    switch (item.type) {
        case 'rating.create':
            return t('notif.rating_new', { name: actor });
        case 'rating.update':
            return t('notif.rating_changed', { name: actor });
        case 'role.grant':
            return t('notif.role_granted', { role: roleLabel(item.payload?.role) });
        case 'role.revoke':
            return t('notif.role_revoked', { role: roleLabel(item.payload?.role) });
        case 'player.link':
            return t('notif.player_linked');
        case 'follow.game': {
            // actor_name у подписок пустой — берём имя из payload.
            const name = item.payload?.player_name?.trim() || actor;
            return t('notif.follow_win', { name });
        }
        case 'streamer.live': {
            const name = item.payload?.player_name?.trim() || actor;
            return t('notif.streamer_live', { name });
        }
        case 'recap':
            return t('notif.recap_title');
        default:
            return item.message;
    }
}

// Человекочитаемые названия ролей для уведомлений.
const ROLE_KEYS: Record<string, TranslationKey> = {
    moderator: 'notif.role_moderator',
    admin: 'notif.role_admin',
    ghost: 'notif.role_ghost',
};

function roleLabel(role?: string): string {
    const key = role ? ROLE_KEYS[role] : undefined;
    return key ? t(key) : (role ?? '');
}

/** Подпись: игрок + раса + какие поля изменились. */
function notificationMeta(item: NotificationItem): string {
    const payload: NonNullable<NotificationItem['payload']> = item.payload ?? {};
    const parts: string[] = [];

    // Дайджест: коротко о том, сколько было нового.
    if (item.type === 'recap') {
        const games = payload.recap?.public?.games ?? 0;
        if (games > 0) parts.push(t('notif.recap_meta', { n: games }));
        return parts.join(' · ');
    }

    // Роли: кто выдал/снял
    const actor = item.actor_name?.trim();
    if (actor && (item.type === 'role.grant' || item.type === 'role.revoke')) {
        parts.push(t('notif.by', { name: actor }));
    }

    if (payload.player_name) {
        parts.push(t('notif.player', { name: payload.player_name }));
    }
    if (payload.race) {
        parts.push(t('notif.race', { race: payload.race }));
    }

    const changed = Array.isArray(payload.changed) ? payload.changed : [];
    const labels = changed
        .map((key) => FIELD_KEYS[key])
        .filter((key): key is TranslationKey => Boolean(key))
        .map((key) => t(key));
    if (labels.length > 0) {
        parts.push(t('notif.changed', { fields: labels.join(', ') }));
    }

    return parts.join(' · ');
}

function formatTime(raw: string): string {
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(getLocale() === 'en' ? 'en-GB' : 'ru-RU', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
}

async function openNotification(item: NotificationItem): Promise<void> {
    if (!state.token) return;

    if (!item.is_read) {
        // Оптимистично уменьшаем счётчик и помечаем строку.
        item.is_read = true;
        unreadCount = Math.max(0, unreadCount - 1);
        updateBadge();
        try {
            await apiRequest(`/api/notifications/${item.id}/read`, {
                method: 'PATCH',
                token: state.token,
            });
        } catch {
            /* не критично: прочитается при следующем открытии */
        }
    }

    closePanel();
    // Дайджест «пока вас не было» — показываем ту же модалку, что при заходе.
    if (item.type === 'recap' && item.payload?.recap) {
        showRecapModal(item.payload.recap);
        return;
    }
    // Стрим открываем в новой вкладке, остальное — внутренним роутом.
    const streamUrl = item.type === 'streamer.live' ? item.payload?.url : null;
    if (streamUrl) {
        window.open(streamUrl, '_blank', 'noopener,noreferrer');
        return;
    }
    if (item.link) navigateTo(item.link);
}

async function markAllRead(): Promise<void> {
    if (!state.token) return;
    try {
        await apiRequest('/api/notifications/read-all', {
            method: 'POST',
            token: state.token,
        });
        unreadCount = 0;
        updateBadge();
        await loadList();
    } catch {
        /* тихо */
    }
}
